import { unwrap } from '@/lib/apiClient'
import {
  listSessionsApiSessionsGet,
  createSessionApiSessionsPost,
  getSessionApiSessionsSessionIdGet,
  renameSessionApiSessionsSessionIdPatch,
  deleteSessionApiSessionsSessionIdDelete,
  togglePinApiSessionsSessionIdPinPost,
  suggestTitleApiSessionsSessionIdTitleSuggestPost,
  shareSessionApiSessionsSessionIdSharePost,
  getSharedSessionApiSharedShareTokenGet,
  editMessageApiSessionsSessionIdMessagesMessageIdPatch,
  postFeedbackApiSessionsSessionIdMessagesMessageIdFeedbackPost,
} from '@/api/generated/api'
import type {
  SessionModel,
  SessionResponse,
  ConversationResponse,
  SharedSessionResponse,
  ShareSessionResponse,
  TitleSuggestResponse,
  FeedbackResponse,
  FeedbackRequestRating,
  CitationModel,
} from '@/api/generated/api'

export type { SessionModel, ConversationResponse, SharedSessionResponse }
export type Citation = CitationModel

// GET /api/sessions?search=
export async function listSessions(search = ''): Promise<SessionModel[]> {
  return unwrap(await listSessionsApiSessionsGet({ search }))
}

// POST /api/sessions
export async function createSession(): Promise<SessionResponse> {
  return unwrap(await createSessionApiSessionsPost())
}

// GET /api/sessions/:id
export async function getSession(id: string): Promise<ConversationResponse> {
  return unwrap(await getSessionApiSessionsSessionIdGet(id))
}

// PATCH /api/sessions/:id
export async function updateSession(id: string, patch: { title: string }): Promise<SessionModel> {
  return unwrap(await renameSessionApiSessionsSessionIdPatch(id, patch))
}

// DELETE /api/sessions/:id
export async function deleteSession(id: string): Promise<void> {
  return unwrap(await deleteSessionApiSessionsSessionIdDelete(id))
}

// POST /api/sessions/:id/pin  (toggles pinned state)
export async function pinSession(id: string): Promise<SessionModel> {
  return unwrap(await togglePinApiSessionsSessionIdPinPost(id))
}

// POST /api/sessions/:id/title/suggest
export async function suggestSessionTitle(
  id: string,
  firstMessage: string
): Promise<TitleSuggestResponse> {
  return unwrap(
    await suggestTitleApiSessionsSessionIdTitleSuggestPost(id, { message: firstMessage })
  )
}

// POST /api/sessions/:id/share
export async function shareSession(id: string): Promise<ShareSessionResponse> {
  return unwrap(await shareSessionApiSessionsSessionIdSharePost(id))
}

// GET /api/shared/:token (no auth — getAuthHeaders() returns {} when signed out)
export async function getSharedSession(token: string): Promise<SharedSessionResponse> {
  return unwrap(await getSharedSessionApiSharedShareTokenGet(token))
}

// PATCH /api/sessions/:id/messages/:messageId
export async function editMessage(
  sessionId: string,
  messageId: string,
  content: string
): Promise<void> {
  // The backend now streams a fresh response from this endpoint; the chat UI
  // ignores the body (it renders the regenerated answer via a separate stream).
  // unwrap() still surfaces non-2xx as ApiError like every other wrapper. On
  // success the mutator hands back the raw SSE Response — cancel its body so we
  // don't leave the stream connection dangling.
  const data = unwrap<unknown>(
    await editMessageApiSessionsSessionIdMessagesMessageIdPatch(sessionId, messageId, { content })
  )
  if (data instanceof Response) {
    await data.body?.cancel().catch(() => {})
  }
}

// POST /api/sessions/:id/messages/:messageId/feedback
export async function submitFeedback(
  sessionId: string,
  messageId: string,
  rating: FeedbackRequestRating,
  comment?: string
): Promise<FeedbackResponse> {
  return unwrap(
    await postFeedbackApiSessionsSessionIdMessagesMessageIdFeedbackPost(sessionId, messageId, {
      rating,
      comment,
    })
  )
}
